import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { searchWorkspaceCompaniesApiWorkspacesWorkspaceIdCompaniesSearchPost } from "@/lib/api/generated/companies/companies";
// industries endpoint will be called via direct HTTP to avoid generated client coupling
import type {
  CompanyResponse,
  DealListItemResponse,
  DealContactListResponse,
  MessageListResponse,
  MessageListItemResponse,
  ListDealMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdDealsDealIdMessagesGetParams,
  ListCompanyMessagesApiWorkspacesWorkspaceIdCompaniesCompanyIdCampaignsCampaignIdMessagesGetParams,
} from "@/lib/api/generated";
import {
  listCountriesApiWorkspacesWorkspaceIdCompaniesCountriesGet,
  listCompanyDealsApiWorkspacesWorkspaceIdCompaniesCompanyIdDealsGet,
  listCompanyContactsApiWorkspacesWorkspaceIdCompaniesCompanyIdContactsGet,
  listCompanyMessagesApiWorkspacesWorkspaceIdCompaniesCompanyIdCampaignsCampaignIdMessagesGet,
} from "@/lib/api/generated/companies/companies";
import { listDealMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdDealsDealIdMessagesGet } from "@/lib/api/generated/campaigns/campaigns";
import { logError, logInfo } from "@/lib/structured-log";
import { listIndustriesWorkspaceLegacy } from "@/lib/api/generated/workspaces/workspaces";

// ========== Types ==========

/**
 * Extended company list response with pagination metadata
 */
export interface CompanyListWithPagination {
  companies: CompanyResponse[];
  total: number;
  limit: number;
  offset: number;
  page: number;
  total_pages: number;
}

const DEFAULT_VISIBLE_TARGET_STATUSES = ["pending", "contacted", "qualified"] as const;

// ========== Schemas ==========

const companiesFiltersSchema = z.object({
  workspaceId: z.string().uuid(),
  industry: z.string().optional(),
  country: z.string().optional(),
  employeeRange: z.string().optional(),
  location: z.string().optional(),
  searchQuery: z.string().optional(),
  statuses: z.array(z.enum(["pending", "contacted", "qualified", "rejected"])).optional(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(20),
});

export type CompaniesFilters = z.infer<typeof companiesFiltersSchema>;

// ========== Server Functions ==========

/**
 * 获取 Companies 列表（使用 search_workspace_companies 端点）
 */
export const getCompaniesListServer = createServerFn({ method: "POST" })
  .validator((data: CompaniesFilters) => companiesFiltersSchema.parse(data))
  .handler(async ({ data }) => {
    const statuses = data.statuses ?? [...DEFAULT_VISIBLE_TARGET_STATUSES];

    logInfo("server.companies.list.started", {
      workspaceId: data.workspaceId,
      page: data.page,
      pageSize: data.pageSize,
      filters: {
        industry: data.industry,
        country: data.country,
        employeeRange: data.employeeRange,
        location: data.location,
        searchQuery: data.searchQuery,
        statuses,
      },
    });

    try {
      const response = await searchWorkspaceCompaniesApiWorkspacesWorkspaceIdCompaniesSearchPost(
        data.workspaceId,
        {
          search_query: data.searchQuery || null,
          industry: data.industry || null,
          country: data.country || null,
          employee_range: data.employeeRange || null,
          statuses,
          page: data.page,
          page_size: data.pageSize,
        },
      );

      if (response.status !== 200) {
        logError("server.companies.list.failed", {
          status: response.status,
          workspaceId: data.workspaceId,
        });
        throw new Error(`Failed to fetch companies list: ${response.status}`);
      }

      // Transform search response to CompanyListResponse format
      // Note: Search endpoint returns aggregated data with simplified company info
      const companies: CompanyResponse[] = response.data.items
        .filter((item) => item.company !== null)
        .map((item) => {
          const company = item.company!;
          const contacts = item.contacts!;
          const target = item.target!;
          const review = item.lead_review!;
          return {
            id: company.id,
            workspace_id: data.workspaceId,
            name: company.name,
            domain: company.domain || null,
            categories: company.categories || [],
            industries: company.industries,
            employee_range: company.employee_range,
            location: company.location || null,
            country: company.country || null,
            annual_revenue: null,
            description: company.description || null,
            logo_url: company.logo_url || null,
            links: company.links,
            campaign_by: item.campaign?.name || null,
            campaign_id: item.campaign?.id || null,
            source: company.source,
            external_id: null,
            contacts: contacts,
            target: target,
            enriched_at: null,
            review: review,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        });

      // Calculate pagination metadata
      const totalPages = Math.ceil(response.data.total / data.pageSize);

      const result = {
        companies,
        total: response.data.total,
        limit: data.pageSize,
        offset: (data.page - 1) * data.pageSize,
        page: data.page,
        total_pages: totalPages,
      };

      logInfo("server.companies.list.completed", {
        workspaceId: data.workspaceId,
        count: result.companies.length,
        total: result.total,
        page: result.page,
        total_pages: result.total_pages,
      });

      return result;
    } catch (error) {
      logError("server.companies.list.error", {
        error: error instanceof Error ? error.message : String(error),
        workspaceId: data.workspaceId,
      });
      throw error;
    }
  });

export const listWorkspaceIndustriesServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<string[]> => {
    logInfo("server.companies.industries.started", {
      workspaceId: data.workspaceId,
    });

    try {
      const res = await listIndustriesWorkspaceLegacy({ workspace_id: data.workspaceId });

      const status = res.status;
      if (status !== 200) {
        logError("server.companies.industries.failed", {
          status,
          workspaceId: data.workspaceId,
        });
        if (status === 422) {
          return [];
        }
        throw new Error(`Failed to list industries: ${status}`);
      }

      const items = res.data ?? [];

      logInfo("server.companies.industries.completed", {
        workspaceId: data.workspaceId,
        count: items.length,
      });

      return items;
    } catch (error) {
      logError("server.companies.industries.error", {
        error: error instanceof Error ? error.message : String(error),
        workspaceId: data.workspaceId,
      });
      return [];
    }
  });

export const listCountriesServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<string[]> => {
    logInfo("server.companies.countries.started", {
      workspaceId: data.workspaceId,
    });

    try {
      const res = await listCountriesApiWorkspacesWorkspaceIdCompaniesCountriesGet(
        data.workspaceId,
      );

      const status = res.status;
      if (status !== 200) {
        logError("server.companies.countries.failed", {
          status,
          workspaceId: data.workspaceId,
        });
        if (status === 422) {
          return [];
        }
        throw new Error(`Failed to list countries: ${status}`);
      }

      const items = res.data ?? [];

      logInfo("server.companies.countries.completed", {
        workspaceId: data.workspaceId,
        count: items.length,
      });

      return items;
    } catch (error) {
      logError("server.companies.countries.error", {
        error: error instanceof Error ? error.message : String(error),
        workspaceId: data.workspaceId,
      });
      return [];
    }
  });

/**
 * 获取指定 Company 的关联 Deals 列表
 */
export const listCompanyDealsServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; companyId: string }) => data)
  .handler(async ({ data }): Promise<DealListItemResponse[]> => {
    logInfo("server.companies.deals.list.started", {
      workspaceId: data.workspaceId,
      companyId: data.companyId,
    });

    try {
      const response = await listCompanyDealsApiWorkspacesWorkspaceIdCompaniesCompanyIdDealsGet(
        data.workspaceId,
        data.companyId,
      );

      if (response.status !== 200) {
        logError("server.companies.deals.list.failed", {
          status: response.status,
          workspaceId: data.workspaceId,
          companyId: data.companyId,
        });
        throw new Error(`Failed to list company deals: ${response.status}`);
      }

      const deals = response.data ?? [];
      logInfo("server.companies.deals.list.completed", {
        workspaceId: data.workspaceId,
        companyId: data.companyId,
        count: deals.length,
      });
      return deals;
    } catch (error) {
      logError("server.companies.deals.list.error", {
        error: error instanceof Error ? error.message : String(error),
        workspaceId: data.workspaceId,
        companyId: data.companyId,
      });
      throw error;
    }
  });

/**
 * 获取指定 Company 的消息聚合（跨关联 Deals）
 */
export const listCompanyMessagesServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      companyId: string;
      campaignId?: string;
      params?:
        | ListDealMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdDealsDealIdMessagesGetParams
        | ListCompanyMessagesApiWorkspacesWorkspaceIdCompaniesCompanyIdCampaignsCampaignIdMessagesGetParams;
    }) => data,
  )
  .handler(async ({ data }): Promise<MessageListResponse> => {
    const page = data.params?.page ?? 1;
    const pageSize = data.params?.page_size ?? 20;
    const order = (data.params?.order_by ?? "desc") as "asc" | "desc";

    logInfo("server.companies.messages.list.started", {
      workspaceId: data.workspaceId,
      companyId: data.companyId,
      page,
      pageSize,
      order,
      campaignId: data.campaignId,
    });

    try {
      if (data.campaignId) {
        const response =
          await listCompanyMessagesApiWorkspacesWorkspaceIdCompaniesCompanyIdCampaignsCampaignIdMessagesGet(
            data.workspaceId,
            data.companyId,
            data.campaignId,
            data.params as ListCompanyMessagesApiWorkspacesWorkspaceIdCompaniesCompanyIdCampaignsCampaignIdMessagesGetParams,
          );

        if (response.status !== 200) {
          logError("server.companies.messages.list.campaign_failed", {
            status: response.status,
            workspaceId: data.workspaceId,
            companyId: data.companyId,
            campaignId: data.campaignId,
          });
          throw new Error(`Failed to list company campaign messages: ${response.status}`);
        }

        return response.data;
      }

      const dealsRes = await listCompanyDealsApiWorkspacesWorkspaceIdCompaniesCompanyIdDealsGet(
        data.workspaceId,
        data.companyId,
      );

      if (dealsRes.status !== 200) {
        logError("server.companies.messages.list.deals_failed", {
          status: dealsRes.status,
          workspaceId: data.workspaceId,
          companyId: data.companyId,
        });
        throw new Error(`Failed to list company deals: ${dealsRes.status}`);
      }

      const deals = dealsRes.data ?? [];

      const perDealParams: ListDealMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdDealsDealIdMessagesGetParams =
        {
          page: 1,
          page_size: pageSize,
          order_by: order,
        };

      const messageResults = await Promise.allSettled(
        deals
          .filter((d) => typeof d.campaign_id === "string" && !!d.campaign_id)
          .map((d) =>
            listDealMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdDealsDealIdMessagesGet(
              data.workspaceId,
              d.campaign_id as string,
              d.id,
              perDealParams,
            ),
          ),
      );

      const aggregated: MessageListItemResponse[] = [];
      let backendTotals = 0;

      for (const r of messageResults) {
        if (r.status === "fulfilled") {
          const resp = r.value;
          if (resp.status === 200) {
            aggregated.push(...(resp.data.items ?? []));
            backendTotals += resp.data.total ?? 0;
          } else {
            logError("server.companies.messages.list.per_deal_failed", { status: resp.status });
          }
        } else {
          logError("server.companies.messages.list.per_deal_error", {
            error: r.reason instanceof Error ? r.reason.message : String(r.reason),
          });
        }
      }

      const getTs = (m: MessageListItemResponse) =>
        (m.sent_at as unknown as string) || (m.received_at as unknown as string) || m.created_at;

      aggregated.sort((a, b) => {
        const at = new Date(getTs(a)).getTime();
        const bt = new Date(getTs(b)).getTime();
        return order === "asc" ? at - bt : bt - at;
      });

      const total = aggregated.length;
      const startIdx = (page - 1) * pageSize;
      const items = aggregated.slice(startIdx, startIdx + pageSize);
      const totalPages = Math.max(1, Math.ceil(total / pageSize));

      logInfo("server.companies.messages.list.completed", {
        workspaceId: data.workspaceId,
        companyId: data.companyId,
        total,
        backendTotals,
        page,
        pageSize,
        totalPages,
      });

      return {
        items,
        total,
        page,
        page_size: pageSize,
        total_pages: totalPages,
      };
    } catch (error) {
      logError("server.companies.messages.list.error", {
        error: error instanceof Error ? error.message : String(error),
        workspaceId: data.workspaceId,
        companyId: data.companyId,
      });
      throw error;
    }
  });

/**
 * 获取指定 Company 的关联 Contacts 列表
 */
export const listCompanyContactsServer = createServerFn({ method: "GET" })
  .validator(
    (data: { workspaceId: string; companyId: string; page?: number; pageSize?: number }) => data,
  )
  .handler(async ({ data }): Promise<DealContactListResponse> => {
    const page = data.page || 1;
    const pageSize = data.pageSize || 50;

    logInfo("server.companies.contacts.list.started", {
      workspaceId: data.workspaceId,
      companyId: data.companyId,
      page,
      pageSize,
    });

    try {
      const response =
        await listCompanyContactsApiWorkspacesWorkspaceIdCompaniesCompanyIdContactsGet(
          data.workspaceId,
          data.companyId,
          { page, page_size: pageSize },
        );

      if (response.status !== 200) {
        logError("server.companies.contacts.list.failed", {
          status: response.status,
          workspaceId: data.workspaceId,
          companyId: data.companyId,
        });
        throw new Error(`Failed to list company contacts: ${response.status}`);
      }

      logInfo("server.companies.contacts.list.completed", {
        workspaceId: data.workspaceId,
        companyId: data.companyId,
        total: response.data.total,
        returned: response.data.items.length,
      });

      return response.data;
    } catch (error) {
      logError("server.companies.contacts.list.error", {
        error: error instanceof Error ? error.message : String(error),
        workspaceId: data.workspaceId,
        companyId: data.companyId,
      });
      throw error;
    }
  });
